import { PriceFeedsAbi } from 'abis/PriceFeeds'
import { atom } from 'jotai'
import { wagmiConfig } from 'services/wallet/chains'
import type { Address } from 'viem'
import { createAtomsWithQuery } from '../atomic-factory'
import { QUERY_KEY_PRICE } from '../keys'
import { DEFAULT_REFETCH_INTERVAL } from '../refetch-intervals'
import {
  type PriceRequest,
  type PriceRequestAtoms,
  type PriceResponse,
  defaultPriceRequest,
  parsePrice,
  priceFeedsContracts,
} from './model'
import { multicall } from '@wagmi/core'
import { NetworkType } from 'abis/set/sets'
import { requestHistoricalPrice } from 'services/price-history/utils'
import Big from 'big.js'
import type {
  Range,
  RangeMap,
  PriceRequest as HistoricalPrices,
} from 'services/price-history/interfaces'

const getPriceFeedsContract = (chainId: number, address: Address) =>
  ({
    address: priceFeedsContracts[chainId][address],
    abi: PriceFeedsAbi,
  }) as const

export const priceAssetsQuery = async <T>({
  queryKey: [, params],
}: {
  queryKey: [string, PriceRequest]
}) => {
  if (params.chainId !== NetworkType.Zeta) {
    const contract = getPriceFeedsContract(params.chainId, params.sellCurrency.address)
    const [[price, decimals]] = await Promise.all([
      await multicall(wagmiConfig, {
        contracts: [
          {
            ...contract,
            functionName: 'latestAnswer',
          },
          {
            ...contract,
            functionName: 'decimals',
          },
        ],
        chainId: params.chainId,
      }),
    ])
    return parsePrice(price, decimals, params) as T
  }
  const now = new Date()
  const end = Math.floor(now.getTime() / 1000)
  const start = Math.floor(now.setUTCHours(0, 0, 0, 0) / 1000)
  const range = [start, end] as Range
  const assetKey = `${params.chainId}:${params.sellCurrency.address.toLowerCase()}`
  const batch: RangeMap = { [assetKey]: [range] }

  const prices: HistoricalPrices = await requestHistoricalPrice(batch)

  const lastKnownPrice = prices[assetKey][0][0].price

  return {
    chainId: params.chainId,
    currency: params.sellCurrency,
    price: Big(lastKnownPrice ?? 1),
  } as T
}

export const priceAssetsFactory = () => {
  const priceRequestParamsAtom = atom<PriceRequest>(defaultPriceRequest)

  const mainProperties = createAtomsWithQuery<
    PriceResponse,
    PriceRequest,
    null,
    null,
    [string, PriceRequest]
  >(
    { first: priceRequestParamsAtom },
    QUERY_KEY_PRICE,
    priceAssetsQuery<PriceResponse>,
    DEFAULT_REFETCH_INTERVAL,
    'priceAssetsQuery',
  )

  return { params: priceRequestParamsAtom, ...mainProperties } as PriceRequestAtoms
}
